import * as _ from 'underscore';
import { isNullOrUndefined } from '../../../../models/utils';
import { Component, HostBinding, ViewChild } from '@angular/core';
import { CellClickedEvent } from 'ag-grid-community';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex, MembershipType, ProjectMemberType } from '../../../../models/enums';
import { CellTemplateType, FilterTemplateType, GenericColDef, GridContext } from '../../../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { MembershipModalComponent } from '../../membership-modal/membership-modal.component';


@Component({
    standalone: false,
    selector: 'res-project-members',
    templateUrl: './project-members.component.html',
    styleUrls: ['./project-members.component.scss']
})
export class ProjectMembersComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    handle: string;
    roleList: Array<any>;
    allMembers: Array<any>;

    originalProjectMembersMap: Map<string, any>;
    hasPermission: boolean;

    isBusy = false;
    isBusyMessage: string;
    isDirty: boolean;
    validChanges = true;

    typeFilterItems: Array<string> = ['All', ...this.commonService.getEnumNames(ProjectMemberType)];

    automationId = 'project-members';
    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Type',
            field: 'type',
            sortBy: 'type',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.nameIconLabel,
            filterType: FilterTemplateType.singleChoice,
            filterItems: this.typeFilterItems,
            filterItemSelected: 'All',
            width: 180
        },
        {
            headerName: 'Username',
            field: 'name',
            sortBy: 'name',
            automationId: this.automationId,
            filterType: FilterTemplateType.freeform,
            lockVisible: true
        },
        {
            headerName: 'First Name',
            field: 'firstName',
            sortBy: 'firstName',
            automationId: this.automationId,
            filterType: FilterTemplateType.freeform
        },
        {
            headerName: 'Last Name',
            field: 'lastName',
            sortBy: 'lastName',
            automationId: this.automationId,
            filterType: FilterTemplateType.freeform
        },
        {
            headerName: 'Email',
            field: 'email',
            sortBy: 'email',
            automationId: this.automationId,
            filterType: FilterTemplateType.freeform
        },
        {
            headerName: 'Role',
            field: 'selectedRole',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.roleDropdown,
            width: 320,
        }
    );
    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.projectManager_listMembers,
        responseName: 'members'
    });
    gridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true,
        uiMultiFilter: true,
        loadAndFilter: true
    });
    rowClassRules = {
        'disabled-row': () => {
            return !this.hasPermission;
        }
    };

    responseCallback = (response: any) => {
        this.isBusy = false;
        if (response) {
            response.members = [];
            if (response.groups) {
                response.groups.forEach(entity => {
                    this.parseMember(entity);
                    response.members.push(entity);
                });
            }
            if (response.users) {
                response.users.forEach(entity => {
                    this.parseMember(entity);
                    response.members.push(entity);
                });
            }
        }
    }

    constructor(private commonService: CommonService, private restService: RestService) { }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string): void {
        this.handle = projectHandle;
        this.hasPermission = Permissions.checkUserPermission(ObjectActionListType.PROJECT_MEMBERS, ActionType.CREATE);
        this.loadData();
    }

    loadData(): void {
        if (this.handle) {
            this.isBusy = true;
            this.originalProjectMembersMap = null;
            this.isBusyMessage = 'Loading Members ...';
            this.restService.post(Endpoint.orgManager_listRoles, {
                objectType: 'ALL'
            }).then(responseListRoles => {
                if (responseListRoles && responseListRoles.roles) {
                    this.roleList = responseListRoles.roles;
                }
                this.restService.post(Endpoint.orgManager_listUsersAndGroups, { organizationName: '' }).then(responseListUsersAndGroups => {
                    if (responseListUsersAndGroups) {
                        this.allMembers = [];
                        if (responseListUsersAndGroups.groups) {
                            responseListUsersAndGroups.groups.forEach(entity => {
                                this.parseMember(entity);
                                this.allMembers.push(entity);
                            });
                        }
                        if (responseListUsersAndGroups.users) {
                            responseListUsersAndGroups.users.forEach(entity => {
                                this.parseMember(entity);
                                if (entity.roles?.length > 0) {
                                    this.allMembers.push(entity);
                                }
                            });
                        }
                    }
                    this.requestInfo.data = {
                        projectHandle: this.handle,
                        count: 0,
                        selectedAttributes: ['role']
                    };
                    this.isBusy = false;
                    this.genericGrid.loadData();
                    this.isDirty = false;
                }, error => {
                    this.isBusy = false;
                });
            }, error => {
                this.isBusy = false;
            });
        }
    }

    onCellClicked = (event: CellClickedEvent) => {
        if (event && event.colDef && (<GenericColDef>event.colDef).cellTemplateType === CellTemplateType.roleDropdown
            && event.data.initialRole && event.data.selectedRole && event.data.initialRole.handle !== event.data.selectedRole.handle) {
            this.isDirty = true;
        }
    }

    parseMember(entity: any): any {
        if (entity) {
            if (entity.userName) {
                entity.type = entity.importedFromSystemUser ? 'System User' : 'User';
                entity.iconClass = `fa-user ${entity.importedFromSystemUser ? ' color-purple-03' : ' color-yellow-07'}`;
                entity.name = entity.userName;
            } else {
                entity.type = entity.importedFromSystemGroup ? 'System Group' : 'Group';
                entity.iconClass = `fa-users ${entity.importedFromSystemGroup ? ' color-purple-03' : ' color-yellow-07'}`;
            }
            if (this.roleList) {
                entity.roleList = this.roleList;
                entity.initialRole = entity.selectedRole = this.getUserRole(entity.roleHandles);
            }
        }
    }

    saveChanges(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (this.handle) {
                const permissionMap = { userRoleSetList: [], groupRoleSetList: [] };
                this.genericGrid.getData().forEach(member => {
                    const item = { name: member.name, roleHandles: [member.selectedRole?.handle] };
                    if (member.type.includes('User')) {
                        permissionMap.userRoleSetList.push(item);
                    } else if (member.type.includes('Group')) {
                        permissionMap.groupRoleSetList.push(item);
                    }
                });
                if (permissionMap.userRoleSetList.length > 0 || permissionMap.groupRoleSetList.length > 0) {
                    this.isBusy = true;
                    this.isBusyMessage = 'Saving Members...';
                    this.restService.post(Endpoint.permissionManager_setPermission, {
                        permissionMap: permissionMap,
                        objectHandle: this.handle
                    }).then(response => {
                        this.isDirty = false;
                        this.isBusy = false;
                        this.originalProjectMembersMap = null;
                        this.loadData();
                        resolve(true);
                    }, error => {
                        this.isBusy = false;
                        resolve(false);
                    });
                } else {
                    resolve(false);
                }
            } else {
                resolve(false);
            }
        });
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadData();
    }

    getUserRole(roleHandles: Array<string>): any {
        if (roleHandles && roleHandles.length > 0) {
            return this.roleList.filter(r => r.handle === roleHandles[0]).pop();
        }
        return null;
    }

    editMembers(): void {
        this.commonService.openModal(MembershipModalComponent, {
            class: 'vertical-percent-80 horizontal-px-1000',
            modalData: { leftList: this.allMembers, rightList: this.genericGrid.getData(), membershipType: MembershipType.project, isProjectMembers: true, isSystemSettingsContext: false }
        }).then((result) => {
            if (result.modalResult && result.modalData.length) {
                const members = [];
                result.modalData.forEach(element => {
                    members.push(element.data);
                });
                this.genericGrid.setData(members);
                this.isDirty = this.dirty();
            }
        });
    }

    dirty(): boolean {
        this.isDirty = false;
        // creates an array of alternating property name, property value
        // and stringify's that array
        if (this.originalProjectMembersMap) {
            const indexes = Array.from(this.originalProjectMembersMap.keys());
            const originalCount = indexes.length;

            const currentProjectMembers = this.genericGrid.getData();
            const currentCount = currentProjectMembers.length;

            if (currentCount !== originalCount) {
                this.isDirty = true;
                return this.isDirty;
            }

            let originalValue = null;
            let currentValue = null;
            for (let i = 0; i < originalCount; i++) {
                originalValue = this.originalProjectMembersMap.get(indexes[i]);
                currentValue = _.findWhere(currentProjectMembers, { name: originalValue.name });

                // the original value no longer exists.
                if (isNullOrUndefined(currentValue)) {
                    this.isDirty = true;
                    break;
                }

                if (currentValue.selectedRole !== currentValue.initialRole) {
                    this.isDirty = true;
                    break;
                }
            }
        }

        return this.isDirty;
    }

    isDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy && !this.genericGrid?.isBusy) {
            if (this.genericGrid?.filters?.length === 0) {
                if (this.dirty()) {
                    isDisabled = false;
                }
            }
        }
        return isDisabled;
    }

    loadDataFinishedCallback = () => {
        if (isNullOrUndefined(this.originalProjectMembersMap)) {
            const projectMembers = this.genericGrid.getData();
            this.originalProjectMembersMap = new Map<string, any>();
            projectMembers.forEach(entity => {
                this.originalProjectMembersMap.set(entity.name, entity);
            });
        }
    }

    openHelp = () => this.commonService.openHelp(HelpIndex.myedisc_case_members_dlg);
}
